import { useMemo } from 'react';
import { DataGrid } from '../../../../ui/data-grid';
import { ColDef } from 'ag-grid-community';
import { useFanGainEntriesForMonth, useFanGainEntriesInDateRange, useFanGainAssignments, useFanGainUserOrder, useRevenueData } from '../../../../../lib/supabase/fan-gain/supabaseFanGainService';
import { useModels } from '../../../../../lib/supabase/models/supabaseModelService';
import { useUsers } from '../../../../../lib/supabase/users/supabaseUserService';
import { DateTime } from 'luxon';
import './fan-gain-spreadsheet.css';

interface FanGainGGSpreadsheetProps {
  defaultMonth: number;
  defaultYear: number;
}

interface RowData {
  id: string;
  model: string;
  model_id?: string;
  isUserHeader?: boolean;
  isTotalRow?: boolean;
  arps?: number;
  [key: string]: string | number | boolean | undefined;
}

interface ModelUserInfo {
  user_id: string;
  userName: string;
  orderIndex: number;
}

interface Model {
  id: string;
  name: string;
}

export function FanGainGGDifferencesSpreadsheet({ defaultMonth, defaultYear }: FanGainGGSpreadsheetProps) {

  const month = useMemo(() => defaultMonth, [defaultMonth]);
  const year = useMemo(() => defaultYear, [defaultYear]);

  const lastMonth = useMemo(() => month === 1 ? 12 : month - 1, [month]);
  const lastMonthYear = useMemo(() => month === 1 ? year - 1 : year, [month, year]);

  const nextMonth = useMemo(() => month === 12 ? 1 : month + 1, [month]);
  const nextMonthYear = useMemo(() => month === 12 ? year + 1 : year, [month, year]);

  const nextMonthFirstDay = useMemo(() => DateTime.utc(nextMonthYear, nextMonth, 1), [nextMonthYear, nextMonth]);

  const lastMonthLastDay = useMemo(() => DateTime.utc(lastMonthYear, lastMonth, 1).endOf('month'), [lastMonthYear, lastMonth]);

  // Fetch data using Supabase
  const { data: allModels = [] } = useModels();
  const { data: users = [] } = useUsers();
  const { data: fanGainAssignments = [] } = useFanGainAssignments();
  const { data: userOrder = [] } = useFanGainUserOrder();
  const { data: fanGainEntries = [] } = useFanGainEntriesForMonth(year, month, 'gg-swaps');
  const { data: nextMonthFirstDayEntries = [] } = useFanGainEntriesInDateRange({
    startDate: nextMonthFirstDay,
    endDate: nextMonthFirstDay,
    type: 'gg-swaps'
  });
  const { data: lastMonthLastDayEntries = [] } = useFanGainEntriesInDateRange({
    startDate: lastMonthLastDay,
    endDate: lastMonthLastDay,
    type: 'gg-swaps'
  });
  const { data: lastMonthRevenueData = {} } = useRevenueData(lastMonth, lastMonthYear);
  const { data: currentMonthRevenueData = {} } = useRevenueData(month, year);

  // Create a map of model assignments to users with order information
  const modelUserMap = useMemo(() => {
    const map = new Map<string, ModelUserInfo & { assignmentIndex: number }>();

    const userOrderMap = new Map(userOrder.map(uo => [uo.user_id, uo.order_index]));

    fanGainAssignments.forEach(assignment => {
      const user = users.find(u => u.id === assignment.user_id);
      const userName = user?.full_name || 'Unknown User';
      const orderIndex = userOrderMap.get(assignment.user_id) || 0;

      map.set(assignment.model_id, {
        user_id: assignment.user_id,
        userName,
        orderIndex,
        assignmentIndex: assignment.index
      });
    });

    return map;
  }, [fanGainAssignments, users, userOrder]);

  const allFilteredModels = useMemo((): Model[] => {
    return allModels
      .filter(model =>
        (model.account_type || 'Normal') === 'Normal' &&
        !model.paused &&
        modelUserMap.has(model.id)
      )
      .sort((a, b) => {
        // Get user assignments for both models
        const userA = modelUserMap.get(a.id);
        const userB = modelUserMap.get(b.id);

        // Models with no assignment go to the end
        if (!userA && !userB) return a.name.localeCompare(b.name);
        if (!userA) return 1;
        if (!userB) return -1;

        // Sort by user orderIndex first, then by assignment index within user
        const orderDiff = userA.orderIndex - userB.orderIndex;
        if (orderDiff !== 0) return orderDiff;
        
        // Within the same user, sort by assignment index
        return userA.assignmentIndex - userB.assignmentIndex;
      })
      .map(model => ({ id: model.id, name: model.name }));
  }, [allModels, modelUserMap]);

  const rowData = useMemo(() => {
    const entriesByModel: { [model_id: string]: { [day: string]: number } } = {};

    // Process current month entries
    fanGainEntries.forEach(entry => {
      const date = DateTime.fromISO(entry.date, { zone: 'UTC' });
      const day = date.day;

      if (!entriesByModel[entry.model_id]) {
        entriesByModel[entry.model_id] = {};
      }

      entriesByModel[entry.model_id][`day${day}`] = entry.value;
    });

    // Create map of next month's first day values
    const nextMonthFirstDayMap: { [model_id: string]: number } = {};
    nextMonthFirstDayEntries.forEach(entry => {
      nextMonthFirstDayMap[entry.model_id] = entry.value;
    });

    // Create map of last month's last day values
    const lastMonthLastDayMap: { [model_id: string]: number } = {};
    lastMonthLastDayEntries.forEach(entry => {
      lastMonthLastDayMap[entry.model_id] = entry.value;
    });

    // Group models by user and create user header rows
    const groupedByUser = new Map<string, { userName: string; models: Model[]; orderIndex: number }>();

    allFilteredModels.forEach(model => {
      const userInfo = modelUserMap.get(model.id);
      if (userInfo) {
        if (!groupedByUser.has(userInfo.user_id)) {
          groupedByUser.set(userInfo.user_id, {
            userName: userInfo.userName,
            models: [],
            orderIndex: userInfo.orderIndex
          });
        }
        groupedByUser.get(userInfo.user_id)!.models.push(model);
      }
    });

    const processedData: RowData[] = [];
    let rowIndex = 0;

    // Sort users by orderIndex
    const sortedUsers = Array.from(groupedByUser.values()).sort((a, b) => a.orderIndex - b.orderIndex);

    // Add total row as the first row
    const totalRow: RowData = {
      id: 'total-row',
      model: 'TOTAL',
      average: 'AVERAGE',
      isTotalRow: true
    };

    const daysInMonth = DateTime.utc(year, month, 1).daysInMonth || 0;

    // Calculate totals for each day (differences: next day - current day)
    for (let day = 1; day <= daysInMonth; day++) {
      let dayDifferenceTotal = 0;

      allFilteredModels.forEach(model => {
        const modelData = entriesByModel[model.id] || {};
        const currentValue = modelData[`day${day}`];

        if (currentValue == null) {
          dayDifferenceTotal += 0;
        } else if (day === 1) {
          const lastMonthLastValue = lastMonthLastDayMap[model.id];
          if (lastMonthLastValue != null) {
            dayDifferenceTotal += currentValue - lastMonthLastValue;
          } else {
            dayDifferenceTotal += 0;
          }
        } else if (day === daysInMonth) {
          const nextMonthFirstValue = nextMonthFirstDayMap[model.id];
          if (nextMonthFirstValue != null) {
            dayDifferenceTotal += nextMonthFirstValue - currentValue;
          } else {
            dayDifferenceTotal += 0;
          }
        } else {
          const nextValue = modelData[`day${day + 1}`];
          if (nextValue == null) {
            dayDifferenceTotal += 0;
          } else {
            dayDifferenceTotal += nextValue - currentValue;
          }
        }
      });

      totalRow[`day${day}`] = dayDifferenceTotal;
    }

    processedData.push(totalRow);
    rowIndex++;

    sortedUsers.forEach(({ userName, models }) => {
      // Add user header row
      const userHeaderRow: RowData = {
        id: `user-${rowIndex}`,
        model: userName,
        isUserHeader: true
      };

      for (let day = 1; day <= daysInMonth; day++) {
        userHeaderRow[`day${day}`] = '';
      }

      processedData.push(userHeaderRow);
      rowIndex++;

      // Add model rows for this user
      models.forEach(model => {
        const modelData = entriesByModel[model.id] || {};
        const row: RowData = {
          id: model.id,
          model: model.name,
          model_id: model.id
        };

        for (let day = 1; day <= daysInMonth; day++) {
          const currentValue = modelData[`day${day}`];

          if (currentValue == null) {
            row[`day${day}`] = 0;
          } else if (day === 1) {
            const lastMonthLastValue = lastMonthLastDayMap[model.id];
            if (lastMonthLastValue != null) {
              row[`day${day}`] = currentValue - lastMonthLastValue;
            } else {
              row[`day${day}`] = 0;
            }
          } else if (day === daysInMonth) {
            const nextMonthFirstValue = nextMonthFirstDayMap[model.id];
            if (nextMonthFirstValue != null) {
              row[`day${day}`] = nextMonthFirstValue - currentValue;
            } else {
              row[`day${day}`] = 0;
            }
          } else {
            const nextValue = modelData[`day${day + 1}`];
            if (nextValue == null) {
              row[`day${day}`] = 0;
            } else {
              row[`day${day}`] = nextValue - currentValue;
            }
          }
        }

        // Calculate ARPS
        const lastMonthRevenue = lastMonthRevenueData[model.id] || 0;
        const currentMonthRevenue = currentMonthRevenueData[model.id] || 0;

        // Calculate average from the row data
        let total = 0;
        let count = 0;
        for (let day = 1; day <= daysInMonth; day++) {
          const value = row[`day${day}`];
          if (typeof value === 'number' && value !== 0) {
            total += value;
            count++;
          }
        }
        const modelAverage = count > 0 ? total / count : 0;

        // Calculate ARPS: (modelRevenueThisMonth - modelRevenueLastMonth) / modelAverage * daysInMonth
        if (modelAverage !== 0) {
          row.arps = (currentMonthRevenue - lastMonthRevenue) / (modelAverage * daysInMonth);
        } else {
          row.arps = 0;
        }

        processedData.push(row);
        rowIndex++;
      });
    });

    return processedData;
  }, [fanGainEntries, nextMonthFirstDayEntries, lastMonthLastDayEntries, allFilteredModels, modelUserMap, year, month, lastMonthRevenueData, currentMonthRevenueData]);

  const daysInMonth = DateTime.utc(year, month, 1).daysInMonth || 0;

  const columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'average',
      pinned: 'left',
      width: 130,
      resizable: false,
      cellStyle: {
        textAlign: 'right',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 700,
        fontSize: '15px',
        textOverflow: 'clip',
        paddingRight: 4,
        color: 'red'
      },
      cellRenderer: (params: any) => {
        if (params.data?.isTotalRow) {
          return 'AVERAGE';
        }
        if (params.data?.isUserHeader) {
          return '';
        }

        // Calculate average for the row
        const daysInMonth = DateTime.utc(year, month, 1).daysInMonth || 0;
        let total = 0;
        let count = 0;
        
        for (let day = 1; day <= daysInMonth; day++) {
          const value = params.data[`day${day}`];
          if (typeof value === 'number' && value !== 0) {
            total += value;
            count++;
          }
        }
        
        const average = count > 0 ? total / count : 0;
        return Math.round(average).toLocaleString();
      }
    },
    {
      headerName: '',
      field: 'model',
      pinned: 'left',
      width: 130,
      resizable: false,
      cellStyle: {
        textAlign: 'left',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 700,
        fontSize: '15px',
        textOverflow: 'clip',
        paddingLeft: 4
      },
      cellRenderer: (params: any) => {
        if (params.data?.isTotalRow) {
          return 'TOTAL DIFF';
        }
        return params.value;
      }
    },
    ...Array.from({ length: daysInMonth }, (_, i) => ({
      headerName: String(i + 1),
      field: `day${i + 1}`,
      width: 60,
      type: 'numericColumn',
      cellClass: 'text-center',
      headerClass: 'day-header',
      editable: false,
      valueParser: (params: any) => {
        const value = params.newValue;
        if (value === '' || value == null) return 0;
        const parsed = Number(value);
        return isNaN(parsed) ? 0 : parsed;
      },
      cellStyle: () => {
        return {
          textAlign: 'right',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 700,
          fontSize: '15px',
          textOverflow: 'clip',
          paddingRight: 4
        };
      },
      valueFormatter: (params: any) => {
        if (typeof params.value === 'number') {
          return params.value.toLocaleString();
        }
        return params.value;
      }
    })),
    {
      headerName: 'ARPS',
      field: 'arps',
      width: 100,
      type: 'numericColumn',
      cellClass: 'text-center',
      headerClass: 'arps-header',
      editable: false,
      cellStyle: {
        textAlign: 'right',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 700,
        fontSize: '15px',
        textOverflow: 'clip',
        paddingRight: 4,
        color: 'blue'
      },
      valueFormatter: (params: any) => {
        if (params.data?.isTotalRow || params.data?.isUserHeader) {
          return '';
        }
        if (typeof params.value === 'number') {
          return Number(params.value).toFixed(2);
        }
        return '';
      }
    }
  ];


  return (
    <div className="space-y-6">
      <div className="flex flex-col">
        <DataGrid
          columnDefs={columnDefs}
          rowData={rowData}
          height={'calc(100vh - 180px)'}
          className="min-h-[400px]"
          gridOptions={{
            domLayout: 'normal',
            rowClassRules: {
              'fan-gain-totals-row': (params) => params.data?.isTotalRow,
              'fan-gain-user-header': (params) => params.data?.isUserHeader
            },
            headerHeight: 42,
            rowHeight: 25,
            getRowId: (p) => String(p.data?.id ?? ''),
            animateRows: false,
          }}
        />
      </div>
    </div>
  );
}

export default FanGainGGDifferencesSpreadsheet;